import mask from '~/utils/mask'
import { useCart, useUiNotification } from '~/composables'
import { generateCustomerData } from './helpers/generateCustomerData'
import { setCustomerCart } from './helpers/setCustomerCart'
import type {
  Customer,
  CustomerUpdateInput,
  MutationUpdateCustomerEmailArgs,
  RequestPasswordResetEmailMutationVariables,
  MutationChangeCustomerPasswordArgs,
  CustomerCreateInput,
} from '@vue-storefront/magento-types'
import type { MutationResetPasswordArgs } from '~/types/magento'
import {
  cartCookieName,
  customerCookieName,
  loginEmailCookieName,
  loginRedirectCookieName,
  phpSessionIdCookieName,
} from '~/enums/cookieNameEnum'
import { handleError } from '~/utils/errorHandler'

interface CustomerState {
  customer: Customer | null
  isLoggedIn: boolean
  isCustomerLoading: boolean
  hasOrders: boolean
}

export const useCustomerStore = defineStore('customer', () => {
  const logger = useAppLogger('useCustomerStore')
  const {
    $magento,
    $i18n: { t, locale },
  } = useNuxtApp()
  const router = useRouter()
  const { setCart } = useCart()
  const { send: sendNotification } = useUiNotification()

  const state: CustomerState = reactive({
    customer: null,
    isLoggedIn: false,
    isCustomerLoading: true,
    hasOrders: false,
  })

  const cartCookie = useCookie(cartCookieName)

  /**
   * Update customer email address.
   */
  const updateCustomerEmail = async (credentials: MutationUpdateCustomerEmailArgs): Promise<void> => {
    logger.debug('updateCustomerEmail', credentials)
    const { errors } = await $magento.updateCustomerEmail(credentials)

    if (errors?.length) {
      throw handleError(errors, 'useCustomerStore', 'updateCustomerEmail')
    }
  }

  /**
   * Update customer details and update customer store.
   */
  const updateCustomer = async ({ customer }: { customer: CustomerUpdateInput }) => {
    logger.debug('updateCustomer', customer)

    const userData = generateCustomerData(customer)

    const { data, errors } = await $magento.updateCustomer(userData)

    if (errors?.length) {
      throw handleError(errors, 'useCustomerStore', 'updateCustomer')
    }

    logger.debug('updateCustomer result', data)

    state.customer = data?.updateCustomerV2?.customer ?? {}
  }

  /**
   * Logout customer and reset cart.
   */
  const logout = async () => {
    const { clearWishlist } = useWishlistStore()
    const customerCookie = useCookie(customerCookieName)
    logger.debug('logout')

    const { errors } = await $magento.revokeCustomerToken()

    if (errors?.length) {
      throw handleError(errors, 'useCustomerStore', 'logout')
    }

    setCart({
      id: '',
      is_virtual: false,
      total_quantity: 0,
      shipping_addresses: [],
      items: [],
      prices: {},
    })

    clearWishlist()
    state.isLoggedIn = false
    state.customer = null
    sessionStorage.setItem('syncedWithMagento', 'false')
    cartCookie.value = null
    customerCookie.value = null
    window.exponea?.anonymize()
  }

  /**
   * Load customer data and update customer store.
   * Log customer out if no customer cookie is found.
   */
  const loadCustomer = async () => {
    logger.debug('loadCustomer')
    state.isCustomerLoading = true
    const customerCookie = useCookie(customerCookieName)
    if (state.isLoggedIn) {
      state.isCustomerLoading = false
      return
    }

    if (!customerCookie.value) {
      state.isLoggedIn = false
      state.customer = null
      state.isCustomerLoading = false
      return null
    }

    const { data, errors } = await $magento.customer()

    if (errors?.length) {
      await logout()
      state.isCustomerLoading = false
      throw handleError(errors, 'useCustomerStore', 'loadCustomer')
    }

    logger.debug('loadCustomer result', data)

    state.isLoggedIn = true
    state.customer = data?.customer ?? {}
    window.exponea?.identify(data?.customer?.email)
    state.isCustomerLoading = false

    return state.customer
  }

  /**
   * Login customer. Handles the following:
   * - Generate customer token
   * - Get customer cart
   * - Set customer cookie
   * - Update customer store
   * - Redirect if login redirect cookie is set
   */
  const login = async ({ email, password, rememberMe }: { email: string; password: string; rememberMe: boolean }) => {
    logger.debug('login')
    const { loadWishlistIds } = useWishlistStore()

    const { data, errors } = await $magento.generateCustomerToken({
      email,
      password,
    })

    if (errors?.length) {
      throw handleError(errors, 'useCustomerStore', 'login')
    }

    logger.debug('login result', data)

    // For some reason the cookie doesn't update in time for $magento.customerCart() if we use useCookie
    document.cookie = `${customerCookieName}=${data?.generateCustomerToken?.token}; max-age=2592000; path=/; Secure; SameSite=Lax`
    // Because the cookie domain has a "." prefix, Nuxt can't seem to be able to handle it with useCookie
    document.cookie = `${phpSessionIdCookieName}=; Max-Age=0`

    await setCustomerCart()

    await loadCustomer()

    state.isLoggedIn = true

    window?.dataLayer?.push({
      event: 'login',
      customer: {
        email: email,
        user_id: state.customer?.id,
      },
    })

    window.exponea.track('login', { area: 'Account' })

    if (rememberMe) {
      const emailCookie = useCookie(loginEmailCookieName, { maxAge: 30 * 24 * 60 * 60 })
      emailCookie.value = email
    }
    const loginRedirectUrl = useCookie(loginRedirectCookieName)
    const relativeLoginRedirectUrl = loginRedirectUrl.value?.replace(location.origin, '')

    loadWishlistIds()

    router.push(relativeLoginRedirectUrl ?? `/${locale.value}/customer/account`).finally(() => {
      loginRedirectUrl.value = null
    })
  }

  /**
   * Register new customer and login.
   */
  const register = async ({
    email,
    password,
    recaptchaToken,
    ...baseData
  }: { is_subscribed_newsletter?: boolean } & CustomerCreateInput) => {
    logger.debug('register')

    const subscribe_newsletter = baseData.is_subscribed_newsletter

    delete baseData.is_subscribed_newsletter

    const { data, errors } = await $magento.createCustomerV2({
      input: {
        email,
        password,
        recaptchaToken,
        ...baseData,
      },
    })

    if (errors?.length) {
      throw handleError(errors, 'useCustomerStore', 'register')
    }

    logger.debug('register result', { data })

    const customer = data?.createCustomerV2?.customer

    window.dataLayer?.push({
      event: 'account_created',
      customer: {
        email: email,
        firstname: baseData.firstname,
        lastname: baseData.lastname,
        user_id: customer.id,
      },
      subscribe_newsletter,
    })

    if (customer?.id) {
      sendNotification({
        id: Symbol('registration_confirmation'),
        message: t('You must confirm your account. Please check your email for the confirmation link.'),
        persist: true,
        title: 'Registration confirmation',
        type: 'success',
        icon: 'check',
      })

      return
    }

    await login({ email, password: password!, rememberMe: false })
  }

  /**
   * Request password reset email.
   */
  const requestPasswordResetEmail = async (params: RequestPasswordResetEmailMutationVariables) => {
    logger.debug('requestPasswordResetEmail', params)
    const { data, errors } = await $magento.requestPasswordResetEmail(params)

    window?.dataLayer?.push({
      event: 'forget_password',
      customer: {
        email: params.email,
      },
    })

    logger.debug('requestPasswordResetEmail result', { data })

    if (errors?.length) {
      if (errors[0].message === "Cannot reset the customer's password") {
        return data
      }
      throw handleError(errors, 'useCustomerStore', 'requestPasswordResetEmail')
    }

    return data
  }

  /**
   * Reset password with token.
   */
  const resetPassword = async (params: MutationResetPasswordArgs) => {
    logger.debug('[Magento] useCustomerStore.resetPassword', {
      id: params.id,
      resetPasswordToken: params.resetPasswordToken,
      newPassword: mask(params.newPassword),
    })
    const { data, errors } = await $magento.MJresetPassword(params)

    if (errors?.length) {
      throw handleError(errors, 'useCustomerStore', 'resetPassword')
    }

    logger.debug('resetPassword result', { data })

    return data
  }

  /**
   * Change customer password and update customer store.
   */
  const changePassword = async (params: MutationChangeCustomerPasswordArgs) => {
    logger.debug('[Magento] useCustomerStore.changePassword', {
      currentPassword: mask(params.currentPassword),
      newPassword: mask(params.newPassword),
    })

    const { data, errors } = await $magento.changeCustomerPassword({
      currentPassword: params.currentPassword,
      newPassword: params.newPassword,
    })

    if (errors?.length) {
      throw handleError(errors, 'useCustomerStore', 'changePassword')
    }

    logger.debug('[Result] ', { data })

    state.customer = data?.changeCustomerPassword
  }

  /**
   * Waits for the user to be logged in by polling `isCustomerLoading` for up to 10 seconds.
   */
  const waitUntilLoggedIn = async () => {
    const start = Date.now()
    while (state.isCustomerLoading && Date.now() - start < 10000) {
      await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)))
    }
  }

  return {
    updateCustomer,
    register,
    login,
    logout,
    changePassword,
    loadCustomer,
    requestPasswordResetEmail,
    resetPassword,
    updateCustomerEmail,
    waitUntilLoggedIn,
    ...toRefs(state),
  }
})

export default useCustomerStore
